import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCreateAccount } from '../../hooks/useCreateAccount';
import { formatError } from '@/shared/api';
import { trackEvent, tagClaritySession } from '@/shared/analytics';
import { trackAccountAdded } from '@/shared/analytics/accountAdded';
import { useFormTracking } from '@/shared/hooks/useFormTracking';
import ErrorAlert from '@/shared/components/ErrorAlert';
import Callout from '@/shared/components/Callout';
import LoadingMessage from './CreationConfirmation/LoadingMessage';
import { CreateAccountResponse } from '@/features/accounts/types';
import ModalButton from '@/shared/components/ModalButton';
import FormField from '@/shared/components/FormField';
import FormInput from '@/shared/components/FormInput';
import ShieldCheckIcon from '@/shared/components/ShieldCheckIcon';

const DESTINATION_COUNTRY_CODE = 'US';

interface FormData {
  email: string;
  password: string;
}

type ConnectionSource = 'dashboard' | 'onboarding';

interface Props {
  onConnected: (response: CreateAccountResponse) => void;
  analyticsSource: ConnectionSource;
}

export default function CreationForm({ onConnected, analyticsSource }: Props) {
  const intl = useIntl();
  const { mutate, isPending } = useCreateAccount();

  const {
    register,
    handleSubmit,
    reset,
    setError,
    getValues,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const { formProps, wrapSubmit, onInvalid } = useFormTracking<FormData>({
    formType: 'add_visa_account',
    clarityStage: 'add_account_page',
    clarityStartedStage: 'add_account_started',
    startEvent: 'add_account_form_started',
    properties: { source: analyticsSource },
    getValues,
    errors,
  });

  const onSubmit = (data: FormData) => {
    trackEvent('visa_account_submit_clicked', {
      destination_country: DESTINATION_COUNTRY_CODE,
      source: analyticsSource,
    });
    tagClaritySession('funnel_stage', 'visa_account_submit_clicked');

    mutate(
      {
        email: data.email,
        password: data.password,
        to_country_code: DESTINATION_COUNTRY_CODE,
      },
      {
        onSuccess: (response) => {
          reset();
          trackAccountAdded({
            accountId: response.account.id,
            destinationCountry: response.account.to_country_code,
            schedulesFound: response.schedules.length,
            source: analyticsSource,
          });
          tagClaritySession('funnel_stage', 'visa_account_added');
          onConnected(response);
        },
        onError: (err) => {
          const errorMessage = formatError(err, intl.formatMessage({ id: 'accounts.createForm.createErrorFallback' }));
          trackEvent('visa_account_add_failed', {
            destination_country: DESTINATION_COUNTRY_CODE,
            error_message: errorMessage,
            source: analyticsSource,
          });
          tagClaritySession('funnel_stage', 'visa_account_add_failed');
          setError('root', {
            message: errorMessage,
          });
        },
      }
    );
  };

  const emailField = register('email', {
    required: intl.formatMessage({ id: 'accounts.createForm.usernameRequired' }),
    pattern: {
      value: /^\S+@\S+$/i,
      message: intl.formatMessage({ id: 'accounts.createForm.usernameInvalid' }),
    },
  });

  const passwordField = register('password', {
    required: intl.formatMessage({ id: 'accounts.createForm.passwordRequired' }),
  });

  return (
    <div>
      <p className="text-sm text-slate-600">
        <FormattedMessage
          id="accounts.createForm.description"
          values={{
            portalLink: (chunks) => (
              <a
                href="https://ais.usvisa-info.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-brand-600 hover:text-brand-500 underline"
              >
                {chunks}
              </a>
            ),
          }}
        />
      </p>

      {errors.root && (
        <div className="mt-4">
          <ErrorAlert message={errors.root.message} />
        </div>
      )}

      <form className="mt-6 grid gap-5 md:grid-cols-2" onSubmit={handleSubmit(wrapSubmit(onSubmit), onInvalid)} {...formProps}>
        <FormField id="visa-email" labelId="accounts.createForm.usernameLabel" error={errors.email?.message}>
          <FormInput
            {...emailField}
            id="visa-email"
            type="text"
            autoComplete="username"
            placeholder={intl.formatMessage({ id: 'accounts.createForm.usernamePlaceholder' })}
            disabled={isPending}
            hasError={!!errors.email}
          />
        </FormField>

        <FormField id="visa-password" labelId="accounts.createForm.passwordLabel" error={errors.password?.message}>
          <FormInput
            {...passwordField}
            id="visa-password"
            type="password"
            autoComplete="new-password"
            placeholder={intl.formatMessage({ id: 'accounts.createForm.passwordPlaceholder' })}
            disabled={isPending}
            hasError={!!errors.password}
          />
        </FormField>

        {isPending ? (
          <div className="md:col-span-2">
            <LoadingMessage />
          </div>
        ) : (
          <div className="md:col-span-2">
            <Callout size="sm" icon={<ShieldCheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-accent-600" />}>
              <span className="font-normal">
                <FormattedMessage id="accounts.createForm.credentialsNotice" />
              </span>
            </Callout>
          </div>
        )}

        <div className="flex justify-end border-t border-slate-200/70 pt-4 md:col-span-2">
          <ModalButton type="submit" disabled={isPending} className="px-5 py-3.5">
            {isPending ? (
              <FormattedMessage id="accounts.createForm.submitCreating" />
            ) : (
              <FormattedMessage id="accounts.createForm.submit" />
            )}
          </ModalButton>
        </div>
      </form>
    </div>
  );
}
